import { MessageAttachmentType } from '../enums/MessageAttachmentType';
import { EventType } from '../enums/EventTypes';
import { LanguageCode } from '../enums/LanguageCodes';
import { UserType } from '../enums/UserTypes';
import { LibraryType } from '../enums/LibraryType';
import { MessageSource } from '../enums/MessageSource';
import { TextClassificationRoutingBehavior } from '../enums/TextClassificationRoutingBehavior';
import { MessageStatus } from '../enums/MessageStatus';
import { HipaaConsentStatus } from '../enums/HipaaConsentStatus';
import { MarketingConsentStatus } from '../enums/MarketingConsentStatus';
import { ChannelType } from '../enums/ChannelTypes';
import { DeviceType } from '../enums/DeviceTypes';
import { AppointmentReminderType } from '../enums/AppointmentType';
import { PrescriptionEventType } from '../enums/PrescriptionEventType';
import { JobType } from '../enums/JobType';
import { HipaaRequestEventType } from '../enums/HipaaRequestEventType';
// We are using string based TS enumerations so we don't get the benefits of ordinal mapping
// or reverse mapping to a value from an ordinal. This class is a workaround for that limitation.
export class MetaTypeService {
  private static initialized: boolean = false;
  private static languageCodes: Map<number, LanguageCode>;
  private static reverseLanguageCodes: Map<LanguageCode, number>;
  private static messageAttachmentTypes : Map<number, MessageAttachmentType>;
  private static reverseMessageAttachmentTypes : Map<MessageAttachmentType, number>;
  private static eventTypes : Map<number, EventType>;
  private static reverseEventTypes : Map<EventType, number>;
  private static userTypes : Map<number, UserType>;
  private static reverseUserTypes : Map<UserType, number>;
  private static messageStatuses : Map<number, MessageStatus>;
  private static reverseMessageStatuses : Map<MessageStatus, number>;
  private static messageSources : Map<number, MessageSource>;
  private static reverseMessageSources : Map<MessageSource, number>;
  private static textClassificationRoutingBehaviors : Map<number, TextClassificationRoutingBehavior>;
  private static reverseTextClassificationRoutingBehaviors : Map<TextClassificationRoutingBehavior, number>;
  private static libraryType : Map<number, LibraryType>;
  private static hipaaConsentStatuses : Map<number, HipaaConsentStatus>;
  private static reverseHipaaConsentStatuses : Map<HipaaConsentStatus, number>;
  private static marketingConsentStatuses : Map<number, MarketingConsentStatus>;
  private static reverseMarketingConsentStatuses : Map<MarketingConsentStatus, number>;
  private static channelTypes : Map<number, ChannelType>;
  private static reverseChannelTypes : Map<ChannelType, number>;
  private static deviceTypes : Map<number, DeviceType>;
  private static reverseDeviceTypes : Map<DeviceType, number>;
  private static appointmentEventTypes : Map<number, AppointmentReminderType>;
  private static reverseAppointmentEventTypes : Map<AppointmentReminderType, number>;
  private static prescriptionEventTypes : Map<number, PrescriptionEventType>;
  private static reversePrescriptionEventTypes : Map<PrescriptionEventType, number>;
  private static jobEventTypes : Map<number, JobType>;
  private static reverseJobEventTypes : Map<JobType, number>;
  private static hipaaRequestEventTypes : Map<number, HipaaRequestEventType>;
  private static reversehipaaRequestEventTypes : Map<HipaaRequestEventType, number>;

  private static reverseMap<TValue, TKey>(mapSource: Map<TKey, TValue>) {
    const mapDestination = new Map<TValue, TKey>();
    mapSource.forEach((value, key) => mapDestination.set(value, key));
    return mapDestination;
  }

  private static initialize() {
    if (MetaTypeService.initialized) return;
    MetaTypeService.languageCodes = new Map<number, LanguageCode>([
      [1, LanguageCode.Afar],
      [2, LanguageCode.Abkhazian],
      [3, LanguageCode.Avestan],
      [4, LanguageCode.Afrikaans],
      [5, LanguageCode.Akan],
      [6, LanguageCode.Amharic],
      [7, LanguageCode.Aragonese],
      [8, LanguageCode.Arabic],
      [9, LanguageCode.Assamese],
      [10, LanguageCode.Avaric],
      [11, LanguageCode.Aymara],
      [12, LanguageCode.Azerbaijani],
      [13, LanguageCode.Bashkir],
      [14, LanguageCode.Belarusian],
      [15, LanguageCode.Bulgarian],
      [16, LanguageCode.Bihari],
      [17, LanguageCode.Bislama],
      [18, LanguageCode.Bambara],
      [19, LanguageCode.Bengali],
      [20, LanguageCode.Tibetan],
      [21, LanguageCode.Breton],
      [22, LanguageCode.Bosnian],
      [23, LanguageCode.Catalan_Valencian],
      [24, LanguageCode.Chechen],
      [25, LanguageCode.Chamorro],
      [26, LanguageCode.Corsican],
      [27, LanguageCode.Cree],
      [28, LanguageCode.Czech],
      [29, LanguageCode.ChurchSlavic_OldSlavonic_ChurchSlavonic_OldBulgarian_OldChurchSlavonic],
      [30, LanguageCode.Chuvash],
      [31, LanguageCode.Welsh],
      [32, LanguageCode.Danish],
      [33, LanguageCode.German],
      [34, LanguageCode.Divehi_Dhivehi_Maldivian],
      [35, LanguageCode.Dzongkha],
      [36, LanguageCode.Ewe],
      [37, LanguageCode.Greek_ModernAfter1453],
      [38, LanguageCode.English],
      [39, LanguageCode.Esperanto],
      [40, LanguageCode.Spanish],
      [41, LanguageCode.Estonian],
      [42, LanguageCode.Basque],
      [43, LanguageCode.Persian],
      [44, LanguageCode.Fulah],
      [45, LanguageCode.Finnish],
      [46, LanguageCode.Fijian],
      [47, LanguageCode.Faroese],
      [48, LanguageCode.French],
      [49, LanguageCode.Western_Frisian],
      [50, LanguageCode.Irish],
      [51, LanguageCode.Gaelic_Scottish_Gaelic],
      [52, LanguageCode.Galician],
      [53, LanguageCode.Guarani],
      [54, LanguageCode.Gujarati],
      [55, LanguageCode.Manx],
      [56, LanguageCode.Hausa],
      [57, LanguageCode.Hebrew],
      [58, LanguageCode.Hindi],
      [59, LanguageCode.Hiri_Motu],
      [60, LanguageCode.Croatian],
      [61, LanguageCode.Haitian_HaitianCreole],
      [62, LanguageCode.Hungarian],
      [63, LanguageCode.Armenian],
      [64, LanguageCode.Herero],
      [65, LanguageCode.Interlingua_InternationalAuxiliaryLanguageAssociation],
      [66, LanguageCode.Indonesian],
      [67, LanguageCode.Interlingue_Occidental],
      [68, LanguageCode.Igbo],
      [69, LanguageCode.Sichuan_Yi_Nuosu],
      [70, LanguageCode.Inupiaq],
      [71, LanguageCode.Ido],
      [72, LanguageCode.Icelandic],
      [73, LanguageCode.Italian],
      [74, LanguageCode.Inuktitut],
      [75, LanguageCode.Japanese],
      [76, LanguageCode.Javanese],
      [77, LanguageCode.Georgian],
      [78, LanguageCode.Kongo],
      [79, LanguageCode.Kikuyu_Gikuyu],
      [80, LanguageCode.Kuanyama_Kwanyama],
      [81, LanguageCode.Kazakh],
      [82, LanguageCode.Kalaallisut_Greenlandic],
      [83, LanguageCode.Central_Khmer],
      [84, LanguageCode.Kannada],
      [85, LanguageCode.Korean],
      [86, LanguageCode.Kanuri],
      [87, LanguageCode.Kashmiri],
      [88, LanguageCode.Kurdish],
      [89, LanguageCode.Komi],
      [90, LanguageCode.Cornish],
      [91, LanguageCode.Kirghiz_Kyrgyz],
      [92, LanguageCode.Latin],
      [93, LanguageCode.Luxembourgish_Letzeburgesch],
      [94, LanguageCode.Ganda],
      [95, LanguageCode.Limburgan_Limburger_Limburgish],
      [96, LanguageCode.Lingala],
      [97, LanguageCode.Lao],
      [98, LanguageCode.Lithuanian],
      [99, LanguageCode.LubaKatanga],
      [100, LanguageCode.Latvian],
      [101, LanguageCode.Malagasy],
      [102, LanguageCode.Marshallese],
      [103, LanguageCode.Maori],
      [104, LanguageCode.Macedonian],
      [105, LanguageCode.Malayalam],
      [106, LanguageCode.Mongolian],
      [107, LanguageCode.Marathi],
      [108, LanguageCode.Malay],
      [109, LanguageCode.Maltese],
      [110, LanguageCode.Burmese],
      [111, LanguageCode.Nauru],
      [112, LanguageCode.Bokmål_Norwegian_Norwegian_Bokmål],
      [113, LanguageCode.Ndebele_North_North_Ndebele],
      [114, LanguageCode.Nepali],
      [115, LanguageCode.Ndonga],
      [116, LanguageCode.Dutch_Flemish],
      [117, LanguageCode.Norwegian_Nynorsk_Nynorsk_Norwegian],
      [118, LanguageCode.Norwegian],
      [119, LanguageCode.Ndebele_South_South_Ndebele],
      [120, LanguageCode.Navajo_Navaho],
      [121, LanguageCode.Chichewa_Chewa_Nyanja],
      [122, LanguageCode.OccitanPost1500],
      [123, LanguageCode.Ojibwa],
      [124, LanguageCode.Oromo],
      [125, LanguageCode.Oriya],
      [126, LanguageCode.Ossetian_Ossetic],
      [127, LanguageCode.Panjabi_Punjabi],
      [128, LanguageCode.Pali],
      [129, LanguageCode.Polish],
      [130, LanguageCode.Pushto_Pashto],
      [131, LanguageCode.Portuguese],
      [132, LanguageCode.Quechua],
      [133, LanguageCode.Romansh],
      [134, LanguageCode.Rundi],
      [135, LanguageCode.Romanian_Moldavian_Moldovan],
      [136, LanguageCode.Russian],
      [137, LanguageCode.Kinyarwanda],
      [138, LanguageCode.Sanskrit],
      [139, LanguageCode.Sardinian],
      [140, LanguageCode.Sindhi],
      [141, LanguageCode.NorthernSami],
      [142, LanguageCode.Sango],
      [143, LanguageCode.Sinhala_Sinhalese],
      [144, LanguageCode.Slovak],
      [145, LanguageCode.Slovenian],
      [146, LanguageCode.Samoan],
      [147, LanguageCode.Shona],
      [148, LanguageCode.Somali],
      [149, LanguageCode.Albanian],
      [150, LanguageCode.Serbian],
      [151, LanguageCode.Swati],
      [152, LanguageCode.Sotho_Southern],
      [153, LanguageCode.Sundanese],
      [154, LanguageCode.Swedish],
      [155, LanguageCode.Swahili],
      [156, LanguageCode.Tamil],
      [157, LanguageCode.Telugu],
      [158, LanguageCode.Tajik],
      [159, LanguageCode.Thai],
      [160, LanguageCode.Tigrinya],
      [161, LanguageCode.Turkmen],
      [162, LanguageCode.Filipino],
      [163, LanguageCode.Tswana],
      [164, LanguageCode.Tonga_TongaIslands],
      [165, LanguageCode.Turkish],
      [166, LanguageCode.Tsonga],
      [167, LanguageCode.Tatar],
      [168, LanguageCode.Twi],
      [169, LanguageCode.Tahitian],
      [170, LanguageCode.Uighur_Uyghur],
      [171, LanguageCode.Ukrainian],
      [172, LanguageCode.Urdu],
      [173, LanguageCode.Uzbek],
      [174, LanguageCode.Venda],
      [175, LanguageCode.Vietnamese],
      [176, LanguageCode.Volapük],
      [177, LanguageCode.Walloon],
      [178, LanguageCode.Wolof],
      [179, LanguageCode.Xhosa],
      [180, LanguageCode.Yiddish],
      [181, LanguageCode.Yoruba],
      [182, LanguageCode.Zhuang_Chuang],
      [183, LanguageCode.Chinese],
    ]);
    MetaTypeService.messageAttachmentTypes = new Map<number, MessageAttachmentType>([
      [5, MessageAttachmentType.IMAGE_JPG],
      [6, MessageAttachmentType.IMAGE_JPEG],
      [7, MessageAttachmentType.IMAGE_PNG],
      [8, MessageAttachmentType.IMAGE_BMP],
      [9, MessageAttachmentType.IMAGE_GIF],
      [45, MessageAttachmentType.APPLICATION_PDF],
      [112, MessageAttachmentType.VIDEO_3GPP],
      [113, MessageAttachmentType.VIDEO_MP4],
      [114, MessageAttachmentType.VIDEO_MPEG],
      [119, MessageAttachmentType.VIDEO_MSASF],
      [115, MessageAttachmentType.VIDEO_OGG],
      [116, MessageAttachmentType.VIDEO_QUICKTIME],
      [117, MessageAttachmentType.VIDEO_WEBM],
      [118, MessageAttachmentType.VIDEO_XM4V],
      [120, MessageAttachmentType.VIDEO_XMSVIDEO],
      [121, MessageAttachmentType.VIDEO_XMSWMV],
      [122, MessageAttachmentType.APPLICATION_OCTET_STREAM],
      [128, MessageAttachmentType.FORM],
      [162, MessageAttachmentType.VCARD],
      [168, MessageAttachmentType.STORY_MENTION],
    ]);
    MetaTypeService.eventTypes = new Map<number, EventType>([
      [26, EventType.Message],
      [27, EventType.Note],
      [28, EventType.SecureMessage],
      [29, EventType.DirectMessage],
      [51, EventType.HipaaConsent],
      [53, EventType.AppointmentReminder],
      [67, EventType.Assignment],
      [90, EventType.RhinopayConsent],
      [103, EventType.SavedContent],
      [104, EventType.MarketingConsent],
      [110, EventType.VideoCall],
      [123, EventType.SavedContentSent],
      [125, EventType.PatientMerge],
      [127, EventType.CopyContent],
      [129, EventType.Rhinoform],
      [132, EventType.ReminderNotification],
      [137, EventType.AudioCall],
      [161, EventType.PrescriptionReminder],
      [169, EventType.HipaaRequest],
      [185, EventType.HipaaManualRequest],
    ]);
    MetaTypeService.userTypes = new Map<number, UserType>([
      [18, UserType.Patient],
      [19, UserType.Member],
      [20, UserType.ConnectedParty],
      [22, UserType.Unknown],
      [36, UserType.Other],
      [135, UserType.Organization],
    ]);
    MetaTypeService.deviceTypes = new Map<number, DeviceType>([
      [68, DeviceType.Desktop],
      [69, DeviceType.Mobile],
    ]);
    MetaTypeService.messageStatuses = new Map<number, MessageStatus>([
      [37, MessageStatus.Queued],
      [38, MessageStatus.Delivered],
      [39, MessageStatus.ConfirmedDelivered],
      [40, MessageStatus.Error],
      [41, MessageStatus.ConfirmedError],
      [165, MessageStatus.Unsent],
    ]);
    MetaTypeService.hipaaConsentStatuses = new Map<number, HipaaConsentStatus>([
      [48, HipaaConsentStatus.Pending],
      [49, HipaaConsentStatus.Granted],
      [50, HipaaConsentStatus.Declined],
    ]);
    MetaTypeService.marketingConsentStatuses = new Map<number, MarketingConsentStatus>([
      [105, MarketingConsentStatus.Pending],
      [106, MarketingConsentStatus.Granted],
      [107, MarketingConsentStatus.Declined],
    ]);
    MetaTypeService.messageSources = new Map<number, MessageSource>([
      [84, MessageSource.SMS],
      [85, MessageSource.Facebook],
      [86, MessageSource.WebForm],
      [164, MessageSource.Instagram],
    ]);
    MetaTypeService.textClassificationRoutingBehaviors = new Map<number, TextClassificationRoutingBehavior>([
      [147, TextClassificationRoutingBehavior.Suggested],
      [148, TextClassificationRoutingBehavior.Automatic],
    ]);
    MetaTypeService.textClassificationRoutingBehaviors = new Map<number, TextClassificationRoutingBehavior>([
      [147, TextClassificationRoutingBehavior.Suggested],
      [148, TextClassificationRoutingBehavior.Automatic],
    ]);
    MetaTypeService.libraryType = new Map<number, LibraryType>([
      [130, LibraryType.SavedContent],
      [131, LibraryType.Form],
    ]);

    MetaTypeService.channelTypes = new Map<number, ChannelType>([
      [10, ChannelType.Sms],
      [11, ChannelType.Landline],
      [12, ChannelType.Twilio],
      [13, ChannelType.Facebook],
      [23, ChannelType.DirectMessage],
      [54, ChannelType.Secure],
      [134, ChannelType.Rhinogram],
      [163, ChannelType.Instagram],
    ]);
    MetaTypeService.appointmentEventTypes = new Map<number, AppointmentReminderType>([
      [65, AppointmentReminderType.Reminder1],
      [66, AppointmentReminderType.Scheduled],
      [143, AppointmentReminderType.Reminder2],
      [171, AppointmentReminderType.Reminder3],
      [172, AppointmentReminderType.Reminder4],
    ]);
    MetaTypeService.prescriptionEventTypes = new Map<number, PrescriptionEventType>([
      [160, PrescriptionEventType.Reminder],
    ]);
    MetaTypeService.jobEventTypes = new Map<number, JobType>([
      [65, JobType.Reminder1],
      [66, JobType.Scheduled],
      [143, JobType.Reminder2],
      [171, JobType.Reminder3],
      [172, JobType.Reminder4],
      [160, JobType.Prescription],
    ]);
    MetaTypeService.hipaaRequestEventTypes = new Map<number, HipaaRequestEventType>([
      [170, HipaaRequestEventType.Message],
    ]);
    // build reverse lookups for each map
    MetaTypeService.reverseLanguageCodes = MetaTypeService.reverseMap(MetaTypeService.languageCodes);
    MetaTypeService.reverseMessageAttachmentTypes = MetaTypeService.reverseMap(MetaTypeService.messageAttachmentTypes);
    MetaTypeService.reverseEventTypes = MetaTypeService.reverseMap(MetaTypeService.eventTypes);
    MetaTypeService.reverseUserTypes = MetaTypeService.reverseMap(MetaTypeService.userTypes);
    MetaTypeService.reverseMessageStatuses = MetaTypeService.reverseMap(MetaTypeService.messageStatuses);
    MetaTypeService.reverseMessageSources = MetaTypeService.reverseMap(MetaTypeService.messageSources);
    MetaTypeService.reverseHipaaConsentStatuses = MetaTypeService.reverseMap(MetaTypeService.hipaaConsentStatuses);
    MetaTypeService.reverseMarketingConsentStatuses = MetaTypeService.reverseMap(MetaTypeService.marketingConsentStatuses);
    MetaTypeService.reverseTextClassificationRoutingBehaviors = MetaTypeService.reverseMap(MetaTypeService.textClassificationRoutingBehaviors);
    MetaTypeService.reverseChannelTypes = MetaTypeService.reverseMap(MetaTypeService.channelTypes);
    MetaTypeService.reverseDeviceTypes = MetaTypeService.reverseMap(MetaTypeService.deviceTypes);
    MetaTypeService.reverseAppointmentEventTypes = MetaTypeService.reverseMap(MetaTypeService.appointmentEventTypes);
    MetaTypeService.reversePrescriptionEventTypes = MetaTypeService.reverseMap(MetaTypeService.prescriptionEventTypes);
    MetaTypeService.reverseJobEventTypes = MetaTypeService.reverseMap(MetaTypeService.jobEventTypes);
    MetaTypeService.reversehipaaRequestEventTypes = MetaTypeService.reverseMap(MetaTypeService.hipaaRequestEventTypes);
    MetaTypeService.initialized = true;
  }

  static toLanguageCode(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.languageCodes.get(type);
  }

  static fromLanguageCode(type: LanguageCode) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseLanguageCodes.get(type);
  }

  static toMessageAttachmentType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.messageAttachmentTypes.get(type);
  }

  static fromMessageAttachmentType(type: MessageAttachmentType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseMessageAttachmentTypes.get(type);
  }

  static toEventType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.eventTypes.get(type);
  }

  static fromEventType(type: EventType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseEventTypes.get(type);
  }

  static toUserType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.userTypes.get(type);
  }

  static fromUserType(type: UserType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseUserTypes.get(type);
  }

  static toMessageStatus(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.messageStatuses.get(type);
  }

  static fromMessageStatus(type: MessageStatus) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseMessageStatuses.get(type);
  }

  static toTextClassificationRoutingType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.textClassificationRoutingBehaviors.get(type);
  }

  static fromTextClassificationRoutingType(type: TextClassificationRoutingBehavior) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseTextClassificationRoutingBehaviors.get(type);
  }

  static toMessageSource(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.messageSources.get(type);
  }

  static fromMessageSource(type: MessageSource) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseMessageSources.get(type);
  }

  static toLibraryType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.libraryType.get(type);
  }

  static toHipaaConsentStatus(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.hipaaConsentStatuses.get(type);
  }

  static toMarketingConsentStatus(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.marketingConsentStatuses.get(type);
  }

  static fromHipaaConsentStatus(type: HipaaConsentStatus) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseHipaaConsentStatuses.get(type);
  }

  static fromMarketingConsentStatus(type: MarketingConsentStatus) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseMarketingConsentStatuses.get(type);
  }

  static toChannelType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.channelTypes.get(type);
  }

  static fromChannelType(type: ChannelType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseChannelTypes.get(type);
  }

  static toDeviceType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.deviceTypes.get(type);
  }

  static fromDeviceType(type: DeviceType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseDeviceTypes.get(type);
  }

  static toAppointmentEventType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.appointmentEventTypes.get(type);
  }

  static fromAppointmentEventType(type: AppointmentReminderType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseAppointmentEventTypes.get(type);
  }

  static toPrescriptionEventType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.prescriptionEventTypes.get(type);
  }

  static fromPrescriptionEventType(type: PrescriptionEventType) {
    MetaTypeService.initialize();
    return MetaTypeService.reversePrescriptionEventTypes.get(type);
  }

  static toJobEventType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.jobEventTypes.get(type);
  }

  static fromJobEventType(type: JobType) {
    MetaTypeService.initialize();
    return MetaTypeService.reverseJobEventTypes.get(type);
  }

  static toHipaaRequestEventType(type: number) {
    MetaTypeService.initialize();
    return MetaTypeService.hipaaRequestEventTypes.get(type);
  }

  static fromHipaaRequestEventType(type: HipaaRequestEventType) {
    MetaTypeService.initialize();
    return MetaTypeService.reversehipaaRequestEventTypes.get(type);
  }
}
